import { useState, useEffect } from 'react';
import { StaffSidebar } from '../../components/StaffSidebar';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Check, X, Clock, MapPin, User, Phone, Mail, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StaffBookings() {
  const { token } = useAuth();
  const { t, isRTL } = useLanguage();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const statusColors = {
    pending: 'bg-golden-hour/20 text-golden-hour border-golden-hour/30',
    confirmed: 'bg-sage/20 text-sage border-sage/30',
    completed: 'bg-deep-charcoal/10 text-deep-charcoal border-deep-charcoal/20',
    cancelled: 'bg-red-100 text-red-600 border-red-200'
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: t('pending'),
      confirmed: t('confirmed'),
      completed: t('completed'),
      cancelled: t('cancelled')
    };
    return labels[status] || status;
  };

  useEffect(() => {
    fetchBookings();
  }, [filter]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const url = filter === 'all' 
        ? `${API}/staff/bookings`
        : `${API}/staff/bookings?status=${filter}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setBookings(response.data);
    } catch (error) {
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (bookingId, status) => {
    try {
      await axios.put(
        `${API}/staff/bookings/${bookingId}`,
        { status },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      toast.success(`Booking ${status}`);
      fetchBookings();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  return (
    <div className={`min-h-screen bg-warm-sand ${isRTL ? 'rtl' : 'ltr'}`}>
      <StaffSidebar />
      
      <main className={`pt-16 lg:pt-0 ${isRTL ? 'lg:mr-64' : 'lg:ml-64'}`}>
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
            <div>
              <h1 className="font-heading text-3xl font-bold text-deep-charcoal">
                {t('bookings')}
              </h1>
              <p className="font-body text-deep-charcoal/60 mt-1">
                {t('manageBookings')}
              </p>
            </div>
            
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40 rounded-xl bg-white border-deep-charcoal/20" data-testid="booking-filter">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allBookings')}</SelectItem>
                <SelectItem value="pending">{t('pending')}</SelectItem>
                <SelectItem value="confirmed">{t('confirmed')}</SelectItem>
                <SelectItem value="completed">{t('completed')}</SelectItem>
                <SelectItem value="cancelled">{t('cancelled')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-terracotta"></div>
            </div>
          ) : bookings.length === 0 ? (
            <Card className="bg-white rounded-xl border-0 shadow-soft">
              <CardContent className="p-12 text-center">
                <Calendar className="w-16 h-16 text-deep-charcoal/20 mx-auto mb-4" />
                <p className="font-body text-deep-charcoal/60">{t('noBookingsFound')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking, index) => (
                <Card 
                  key={booking.booking_id}
                  className="bg-white rounded-xl border-0 shadow-soft overflow-hidden animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`booking-row-${booking.booking_id}`}
                >
                  <CardContent className="p-6">
                    <div className={`flex flex-col lg:flex-row lg:items-center justify-between gap-6 ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
                      {/* Booking Info */}
                      <div className="flex-1 space-y-4">
                        <div className={`flex items-center gap-3 flex-wrap ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <h3 className="font-heading text-xl font-semibold text-deep-charcoal">
                            {booking.service_name}
                          </h3>
                          <Badge className={`${statusColors[booking.status]} border capitalize`}>
                            {getStatusLabel(booking.status)}
                          </Badge>
                        </div>
                        
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                          <div className={`flex items-center text-deep-charcoal/70 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <User className={`w-4 h-4 text-terracotta ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span className="font-body text-sm">{booking.customer_name}</span>
                          </div>
                          <div className={`flex items-center text-deep-charcoal/70 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Mail className={`w-4 h-4 text-terracotta ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span className="font-body text-sm truncate">{booking.customer_email}</span>
                          </div>
                          <div className={`flex items-center text-deep-charcoal/70 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Phone className={`w-4 h-4 text-terracotta ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span className="font-body text-sm">{booking.customer_phone}</span>
                          </div>
                          <div className={`flex items-center text-deep-charcoal/70 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <MapPin className={`w-4 h-4 text-terracotta ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span className="font-body text-sm truncate">{booking.branch_name}</span>
                          </div>
                        </div>

                        <div className={`flex items-center gap-6 text-deep-charcoal/70 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Calendar className={`w-4 h-4 text-sage ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span className="font-body text-sm">{booking.booking_date}</span>
                          </div>
                          <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Clock className={`w-4 h-4 text-sage ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span className="font-body text-sm">{booking.booking_time}</span>
                          </div>
                          {booking.guest_count > 1 && (
                            <span className="font-body text-sm">
                              {booking.guest_count} {t('guests')}
                            </span>
                          )}
                        </div>

                        {booking.special_requests && (
                          <p className="font-body text-sm text-deep-charcoal/60 italic bg-warm-sand/50 p-3 rounded-lg">
                            "{booking.special_requests}"
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      {booking.status === 'pending' && (
                        <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <Button
                            onClick={() => updateStatus(booking.booking_id, 'confirmed')}
                            className="rounded-xl bg-sage text-white hover:bg-sage-dark"
                            data-testid={`confirm-${booking.booking_id}`}
                          >
                            <Check className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            {t('confirm')}
                          </Button>
                          <Button
                            onClick={() => updateStatus(booking.booking_id, 'cancelled')}
                            variant="outline"
                            className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
                            data-testid={`cancel-${booking.booking_id}`}
                          >
                            <X className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            {t('cancel')}
                          </Button>
                        </div>
                      )}
                      
                      {booking.status === 'confirmed' && (
                        <Button
                          onClick={() => updateStatus(booking.booking_id, 'completed')}
                          className="rounded-xl bg-deep-charcoal text-white hover:bg-deep-charcoal/90"
                          data-testid={`complete-${booking.booking_id}`}
                        >
                          <Check className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          {t('markComplete')}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
